import {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { create } from 'zustand';
import { SHARE_CAPABILITY_HEADER, hasHeader, setHeader } from 'api/axiosConfig';
import { getOriginalAxiosRequestConfig } from 'api/requestErrorMetadata';
import { getShareLinks } from 'api/generated/api/share-links';
import type { ShareOperation, ShareSessionResponse } from 'api/generated/types';
import { getApiErrorCode, getApiErrorStatus } from 'utils/apiError';

/**
 * A share-link viewer's session: the secret in the URL fragment is exchanged for a short-lived
 * capability that every request on the shared page carries in `x-share-capability`. The
 * capability lives only in this module's memory; the fragment stays in the address bar so a
 * reload exchanges it again.
 */

export type ShareSessionStatus =
  | 'loading'
  | 'ready'
  | 'invalid'
  | 'expired'
  | 'revoked'
  | 'error';

/** The shared report the session names. */
interface ShareSessionInfo {
  shareLinkId: string;
  orgId: string;
  candidateId: string;
  campaignId: string;
  callId: string;
  operations: readonly ShareOperation[];
  expiresAt: string;
}

interface ShareSessionState {
  status: ShareSessionStatus;
  session: ShareSessionInfo | null;
}

const INITIAL_STATE: ShareSessionState = { status: 'loading', session: null };

export const useShareSessionStore = create<ShareSessionState>()(
  () => INITIAL_STATE
);

const SHARE_SESSION_EXCHANGE_PATH = '/share_links/session';

// Bounds of ShareSessionRequest.secret; anything else cannot be a secret, so it is refused
// without a request.
const MIN_SECRET_LENGTH = 32;
const MAX_SECRET_LENGTH = 128;
const URL_SAFE_SECRET = /^[A-Za-z0-9_-]+$/;

/** Fraction of the capability's lifetime after which it is renewed. */
const RENEWAL_FRACTION = 0.8;
/** Floor on the renewal delay, so a lifetime at or near zero cannot spin. */
export const MIN_RENEWAL_DELAY_MS = 5_000;
/** Delay before retrying a renewal that failed for a reason other than the link itself. */
const RENEWAL_RETRY_DELAY_MS = 30_000;

const SESSION_EXPIRED_CODE = 'SHARING_SESSION_EXPIRED';

const STATUS_BY_CODE: Readonly<Record<string, ShareSessionStatus>> = {
  SHARING_LINK_INVALID: 'invalid',
  SHARING_LINK_EXPIRED: 'expired',
  SHARING_LINK_REVOKED: 'revoked',
};

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Set on the single retry after the server rejected an expired share capability. */
    shareCapabilityRetry?: boolean;
  }
}

/** Pure: the share secret in a location hash (`#<secret>`), or null when it cannot be one. */
export function readShareSecret(hash: string): string | null {
  const secret = hash.startsWith('#') ? hash.slice(1) : hash;
  if (
    secret.length < MIN_SECRET_LENGTH ||
    secret.length > MAX_SECRET_LENGTH ||
    !URL_SAFE_SECRET.test(secret)
  ) {
    return null;
  }
  return secret;
}

/**
 * Pure: milliseconds after receiving a capability that lives `expiresInSeconds` until it should
 * be renewed. Counting from receipt, rather than comparing `expires_at` with the local clock,
 * keeps a skewed clock from renewing too late or too often.
 */
export function renewalDelayMs(expiresInSeconds: number): number {
  if (!Number.isFinite(expiresInSeconds)) return MIN_RENEWAL_DELAY_MS;
  return Math.max(
    MIN_RENEWAL_DELAY_MS,
    Math.floor(expiresInSeconds * 1000 * RENEWAL_FRACTION)
  );
}

/**
 * Pure: the terminal status a coded link rejection maps to (invalid, expired or revoked), or
 * null for any other failure.
 */
function linkRejectionStatus(error: unknown): ShareSessionStatus | null {
  if (getApiErrorStatus(error) !== 401) return null;
  const code = getApiErrorCode(error);
  return (code && STATUS_BY_CODE[code]) || null;
}

/** Pure: whether the server refused the request because its capability expired. */
function isShareSessionExpiredError(error: unknown): boolean {
  return (
    getApiErrorStatus(error) === 401 &&
    getApiErrorCode(error) === SESSION_EXPIRED_CODE
  );
}

/** Pure: the session a share-session response names, or null when it names no report. */
function sessionFromResponse(
  response: ShareSessionResponse
): ShareSessionInfo | null {
  if (!response.call_id) return null;
  return {
    shareLinkId: response.share_link_id,
    orgId: response.org_id,
    candidateId: response.candidate_id,
    campaignId: response.campaign_id,
    callId: response.call_id,
    operations: response.operations,
    expiresAt: response.expires_at,
  };
}

/** Pure: whether `url` is the exchange itself, which must never carry a capability. */
function isExchangeUrl(url: string | undefined): boolean {
  if (!url) return false;
  return (
    new URL(url, window.location.origin).pathname ===
    SHARE_SESSION_EXCHANGE_PATH
  );
}

// The session's private state. `generation` changes whenever the session starts over or
// stops, so an exchange that resolves afterwards is discarded. `renewalDue` marks a timed
// renewal skipped while the page was hidden, which runs when it is shown again.
let secret: string | null = null;
let capability: string | null = null;
let generation = 0;
let renewalTimer: ReturnType<typeof setTimeout> | null = null;
let renewalDue = false;
let initialExchange: Promise<void> | null = null;
let renewal: Promise<boolean> | null = null;

const clearRenewalTimer = (): void => {
  renewalDue = false;
  if (renewalTimer !== null) {
    clearTimeout(renewalTimer);
    renewalTimer = null;
  }
};

// A hidden tab does not renew on a timer, so a link left open in a background tab is not
// exchanged around the clock. A request that meets an expired capability still renews it.
const onVisibilityChange = (): void => {
  if (document.visibilityState === 'hidden' || !renewalDue) return;
  renewalDue = false;
  void refreshShareSession();
};

const scheduleRenewal = (delayMs: number, forGeneration: number): void => {
  clearRenewalTimer();
  renewalTimer = setTimeout(() => {
    renewalTimer = null;
    if (forGeneration !== generation) return;
    if (document.visibilityState === 'hidden') {
      renewalDue = true;
      return;
    }
    void refreshShareSession();
  }, delayMs);
};

const endWithStatus = (status: ShareSessionStatus): void => {
  clearRenewalTimer();
  capability = null;
  useShareSessionStore.setState({ status, session: null });
};

// A renewal presents the page's current capability; the server counts the exchange as an open
// unless that capability is still valid for the same link.
const exchange = (
  forSecret: string,
  currentCapability: string | null
): Promise<ShareSessionResponse> =>
  getShareLinks().exchangeShareLinkSession(
    currentCapability === null
      ? { secret: forSecret }
      : { secret: forSecret, capability: currentCapability }
  );

/** Apply a successful exchange: keep the capability and schedule its renewal. */
const accept = (response: ShareSessionResponse, forGeneration: number) => {
  const session = sessionFromResponse(response);
  if (!session) {
    console.warn('[shareSession] exchange named no report', {
      shareLinkId: response.share_link_id,
      kind: response.kind,
    });
    endWithStatus('invalid');
    return;
  }
  capability = response.capability;
  useShareSessionStore.setState({ status: 'ready', session });
  scheduleRenewal(renewalDelayMs(response.expires_in_seconds), forGeneration);
};

/**
 * Start the session for the secret in `hash` (the page's `location.hash`). Starting again with
 * the same secret while a session is loading or ready reuses it; `force` exchanges again (the
 * error state's retry).
 */
export function startShareSession(
  hash: string,
  { force = false }: { force?: boolean } = {}
): Promise<void> {
  const nextSecret = readShareSecret(hash);
  const { status } = useShareSessionStore.getState();
  if (
    !force &&
    nextSecret !== null &&
    nextSecret === secret &&
    (status === 'ready' || status === 'loading')
  ) {
    return initialExchange ?? Promise.resolve();
  }

  stopShareSession();
  if (nextSecret === null) {
    endWithStatus('invalid');
    return Promise.resolve();
  }

  secret = nextSecret;
  const forGeneration = generation;
  document.addEventListener('visibilitychange', onVisibilityChange);
  useShareSessionStore.setState({ status: 'loading', session: null });
  const run = exchange(nextSecret, null)
    .then((response) => {
      if (forGeneration !== generation) return;
      accept(response, forGeneration);
    })
    .catch((error: unknown) => {
      if (forGeneration !== generation) return;
      const rejected = linkRejectionStatus(error);
      if (!rejected) console.error('[shareSession] exchange failed', error);
      endWithStatus(rejected ?? 'error');
    })
    .finally(() => {
      if (forGeneration === generation) initialExchange = null;
    });
  initialExchange = run;
  return run;
}

/**
 * Exchange the secret again for a fresh capability (renewal, or after a request met an expired
 * one). Concurrent callers share one exchange. Resolves whether a capability is available.
 */
function refreshShareSession(): Promise<boolean> {
  if (renewal) return renewal;
  const forSecret = secret;
  if (forSecret === null) return Promise.resolve(false);
  const forGeneration = generation;
  const run = exchange(forSecret, capability)
    .then((response) => {
      if (forGeneration !== generation) return false;
      accept(response, forGeneration);
      return capability !== null;
    })
    .catch((error: unknown) => {
      if (forGeneration !== generation) return false;
      const rejected = linkRejectionStatus(error);
      if (rejected) {
        endWithStatus(rejected);
        return false;
      }
      // The link may still be good: keep the current capability (requests that meet its
      // expiry ask again) and try again later.
      console.warn('[shareSession] renewal failed; retrying later', error);
      scheduleRenewal(RENEWAL_RETRY_DELAY_MS, forGeneration);
      return false;
    })
    .finally(() => {
      if (renewal === run) renewal = null;
    });
  renewal = run;
  return run;
}

/** End the session: forget the secret and capability and cancel renewal. */
export function stopShareSession(): void {
  generation += 1;
  clearRenewalTimer();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  secret = null;
  capability = null;
  initialExchange = null;
  renewal = null;
  useShareSessionStore.setState(INITIAL_STATE);
}

/** The current capability, or null while there is none. */
export function getShareCapability(): string | null {
  return capability;
}

/** Stamp the current capability on a request (never on the exchange). */
function applyShareCapability(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  const token = capability;
  if (!token || isExchangeUrl(config.url)) return config;
  setHeader(config, SHARE_CAPABILITY_HEADER, token);
  return config;
}

/**
 * Route a failed request's error: an expired capability is renewed and the request retried
 * once; a revoked, expired or invalid link ends the session so the page shows why.
 */
async function handleShareRequestError(
  instance: AxiosInstance,
  error: AxiosError
): Promise<AxiosResponse> {
  const config = getOriginalAxiosRequestConfig(error);
  if (!config || !hasHeader(config.headers, SHARE_CAPABILITY_HEADER))
    throw error;

  if (isShareSessionExpiredError(error) && !config.shareCapabilityRetry) {
    const renewed = await refreshShareSession();
    const token = capability;
    if (!renewed || !token) throw error;
    const headers = AxiosHeaders.from(config.headers);
    headers.set(SHARE_CAPABILITY_HEADER, token);
    return instance.request({ ...config, headers, shareCapabilityRetry: true });
  }

  const rejected = linkRejectionStatus(error);
  if (rejected) {
    endWithStatus(rejected);
  }
  throw error;
}

/**
 * Install the request and response interceptors that carry the capability on `instance`.
 * Returns the function that removes them.
 */
export function installShareSessionInterceptors(
  instance: AxiosInstance
): () => void {
  const requestId = instance.interceptors.request.use(applyShareCapability);
  const responseId = instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => handleShareRequestError(instance, error)
  );
  return () => {
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
  };
}
